import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import PropTypes from "prop-types";

// Being signed in is not enough to reach /admin: access is carried by the
// `admin` custom claim that scripts/set-admin.js sets. The same claim gates
// firestore.rules and storage.rules, so the UI check and the server check
// cannot drift apart.
const ProtectedRoute = (props) => {
  const { children } = props;
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setStatus("signed-out");
        return;
      }

      try {
        // Force a refresh so a claim granted since the last sign-in is seen
        // without the user having to clear their session.
        const token = await user.getIdTokenResult(true);
        setStatus(token.claims.admin === true ? "admin" : "not-admin");
      } catch (error) {
        console.error("Could not verify admin access:", error);
        setStatus("not-admin");
      }
    });

    return () => unsubscribe();
  }, []);

  if (status === "checking") {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === "admin") {
    return children;
  }

  // A signed-in non-admin is sent back to the login screen with a reason, so
  // they are not bounced silently in a loop.
  return <Navigate to="/login" replace state={{ reason: status }} />;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProtectedRoute;
